import * as React from 'react'
import env from '../../../core/config/env'
import Paper from '@mui/material/Paper'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Button,
  Stack,
  Select,
  MenuItem,
} from '@mui/material'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import CheckoutIcon from '@mui/icons-material/ContentCopy'
import { useNavigate } from 'react-router-dom'
import { dateTimeString } from '../../../core/functions/dateTime'
import Hosts from '../../../core/functions/hosts'
import { formatarPhone, linkWhatsApp } from '../../../core/functions/phoneUtils'
import { useCheckoutOpen } from '../../../core/hooks/useCheckoutOpen'
import TablePaginationDefault from '../../../components/Tables/TablePaginationDefault'
import { useQuery } from '../../../components/Query/useQuery'
import {
  ExportFileTypeEnum,
  ReportDownloadController,
} from '../../../core/controllers/ReportDownloadController'
import { useDownloadFile } from '../../../core/hooks/useDownloadFile'
import { ExportToFile } from '../ExportToFile'
import ErrorSnackbar from '../../../components/Snackbar/ErrorSnackbar'
import LoadingBackdrop from '../../../components/Loading/LoadingBackdrop'
import CopiedSnackbar from '../../../components/Snackbar/CopiedSnackbar'
import InputSearch from '../../../components/Inputs/InputSearch'
import GetAppIcon from '@mui/icons-material/GetApp'
import OrderController from '../../../core/controllers/OrderController'
import AlertDefault from '../../../components/Alerts'
import { AsaasSeal } from '../../../components/AsaasSeal/AsaasSeal'

interface Column {
  id:
    | 'createdDate'
    | 'name'
    | 'email'
    | 'productName'
    | 'phone'
    | 'codeId'
    | 'paymentyMethod'
  label: string
  minWidth?: number
  align?: 'right'
  format?: (value: number) => string
}

const columns: readonly Column[] = [
  { id: 'createdDate', label: 'Criado', minWidth: 80 },
  { id: 'name', label: 'Nome', minWidth: 100 },
  {
    id: 'productName',
    label: 'Produto',
    minWidth: 100,
  },
  {
    id: 'phone',
    label: 'Telefone',
    minWidth: 100,
  },
  {
    id: 'codeId',
    label: 'Checkout',
    minWidth: 60,
  },
  {
    id: 'paymentyMethod',
    label: 'Link',
    minWidth: 60,
  },
]

export default function CheckoutOpenPage() {
  const query = useQuery()

  const report =
    (query.get('type') || 'order') === 'order' ? 'order' : 'subscription'

  const [page, setPage] = React.useState(0)
  const [rowsPerPage, setRowsPerPage] = React.useState(10)
  const [search, setSearch] = React.useState('')
  const [lastDays, setLastDays] = React.useState(30)
  const [copied, setCopied] = React.useState(false)
  const [errorSending, setErrorSending] = React.useState('')

  const { loading, data, error, execute, clear } = useCheckoutOpen({
    page,
    rows: rowsPerPage,
    search,
    lastDays,
    report,
  })

  const navigate = useNavigate()

  const handleChangePage = (newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (rows: number) => {
    setRowsPerPage(rows)
    setPage(0)
  }

  const handleDownloadFile = (typeFile: ExportFileTypeEnum) => {
    return ReportDownloadController.getOrdersOpen({
      fileType: typeFile,
      search,
      lastDays,
      report,
      type: '',
    })
  }

  const handlerDownloadingError = (e: string) => setErrorSending(e)
  const getFileName = (type: ExportFileTypeEnum) =>
    `report_${new Date().toISOString().replace(':', '_').replace('.', '_')}.${
      type === ExportFileTypeEnum.XLS ? 'xlsx' : 'csv'
    }`

  const { download, downloading } = useDownloadFile({
    apiDefinition: handleDownloadFile,
    onError: handlerDownloadingError,
    getFileName,
  })

  const downloadCallback = React.useCallback(
    (type: ExportFileTypeEnum) => download(type),
    [download]
  )

  const handleConvertToXLS = async () => {
    downloadCallback(ExportFileTypeEnum.XLS)
  }

  const handleConvertToCSV = async () => {
    downloadCallback(ExportFileTypeEnum.CSV)
  }

  const handleDownloadBoleto = async (codeId: string, chargeId: string) => {
    const boletoURL = await OrderController.getPaymentLink({ codeId, chargeId })
    if (!boletoURL.success) {
      setErrorSending(boletoURL.error || 'Erro ao copiar código')
      return
    }
    window.open(boletoURL.data, '_blank')
  }

  const handleCopyPixCode = async (codeId: string, chargeId: string) => {
    const pixCode = await OrderController.getPaymentLink({ codeId, chargeId })
    if (!pixCode.success) {
      setErrorSending(pixCode.error || 'Erro ao copiar código PIX')
      return
    }
    Hosts.copyTextToClipboard(pixCode.data)
    setCopied(true)
  }

  return (
    <Box>
      <Typography variant="h5" fontWeight={500} sx={{ mb: 2 }}>
        {report === 'order' ? 'Vendas ' : 'Assinaturas '} Abertas
      </Typography>

      {error !== '' && (
        <Box sx={{ mb: 2 }}>
          <AlertDefault onClose={clear} severity="error" sx={{ width: '100%' }}>
            {error}
          </AlertDefault>
        </Box>
      )}

      <Stack direction="row" spacing={1}>
        <InputSearch
          name="Filtrar por nome"
          setSearch={setSearch}
          execute={execute}
        />

        <FormControl sx={{ width: '20%' }}>
          <InputLabel id="days-select-label">Últimos</InputLabel>
          <Select
            variant="outlined"
            labelId="days-select-label"
            label="Últimos"
            defaultValue={30}
            value={lastDays}
            onChange={(e) => setLastDays(Number(e.target.value))}
          >
            <MenuItem value={30}>30 dias</MenuItem>
            <MenuItem value={60}>60 dias</MenuItem>
            <MenuItem value={90}>90 dias</MenuItem>
            <MenuItem value={180}>180 dias</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      <Stack direction="row">
        <ExportToFile
          convertToCSV={handleConvertToCSV}
          convertToXLS={handleConvertToXLS}
        />
      </Stack>

      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer>
          <Table stickyHeader aria-label="sticky table">
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    align={column.align}
                    style={{ minWidth: column.minWidth }}
                  >
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {data.items.map((row, index) => {
                const pathAffiliated =
                  row.affiliatedCode && row.affiliatedCode !== ''
                    ? `?afid=${row.affiliatedCode}`
                    : ''
                const url = `${env.PAY_HOST}/c/${row['codeId']}${pathAffiliated}`
                return (
                  <TableRow
                    hover
                    role="checkbox"
                    tabIndex={-1}
                    key={`${index}-${row.codeId}}`}
                  >
                    {columns.map((column) => {
                      const value = row[column.id]
                      if (column.id === 'createdDate') {
                        return (
                          <TableCell key={column.id} align={column.align}>
                            {dateTimeString(new Date(value))}
                          </TableCell>
                        )
                      }
                      if (column.id === 'phone') {
                        return (
                          <TableCell key={column.id} align={column.align}>
                            <Button
                              variant="text"
                              size="small"
                              startIcon={<WhatsAppIcon />}
                              onClick={() => {
                                const waLink = linkWhatsApp(
                                  value?.toString(),
                                  row.phoneCountryCode,
                                  row.countryCode
                                )
                                if (waLink)
                                  Hosts.openNewBlank(
                                    `${waLink}?text=${encodeURIComponent(url)}`
                                  )
                              }}
                            >
                              {formatarPhone(
                                value?.toString(),
                                row.phoneCountryCode,
                                row.countryCode
                              )}
                            </Button>
                          </TableCell>
                        )
                      }
                      if (column.id === 'codeId') {
                        return (
                          <TableCell key={column.id} align={column.align}>
                            <Button
                              variant="text"
                              size="small"
                              startIcon={<CheckoutIcon />}
                              onClick={async () => {
                                await Hosts.copyTextToClipboard(url)
                                setCopied(true)
                              }}
                            >
                              {url}
                            </Button>
                          </TableCell>
                        )
                      }
                      if (column.id === 'name') {
                        return (
                          <TableCell key={column.id} align={column.align}>
                            <Stack direction="column">
                              <Typography variant="subtitle1">
                                {value.toString()}
                              </Typography>
                              <Typography variant="subtitle2">
                                {row['email']}
                              </Typography>
                            </Stack>
                          </TableCell>
                        )
                      }
                      if (column.id === 'paymentyMethod') {
                        return (
                          <TableCell key={column.id} align={column.align}>
                            {row.paymentyMethod === 'boleto' ? (
                              <Button
                                variant="text"
                                size="small"
                                startIcon={<GetAppIcon />}
                                onClick={() =>
                                  handleDownloadBoleto(
                                    row.codeId,
                                    row.orderChargeId
                                  )
                                }
                              >
                                Baixar Boleto
                              </Button>
                            ) : row.paymentyMethod === 'pix' ? (
                              <Button
                                variant="text"
                                size="small"
                                startIcon={<CheckoutIcon />}
                                onClick={() =>
                                  handleCopyPixCode(
                                    row.codeId,
                                    row.orderChargeId
                                  )
                                }
                              >
                                Copiar PIX
                              </Button>
                            ) : null}
                          </TableCell>
                        )
                      }
                      return (
                        <TableCell key={column.id} align={column.align}>
                          {column.format && typeof value === 'number'
                            ? column.format(value)
                            : value.toString()}
                        </TableCell>
                      )
                    })}
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePaginationDefault
          count={data.total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      <LoadingBackdrop open={loading} />

      <ErrorSnackbar
        open={errorSending !== ''}
        onClose={() => setErrorSending('')}
      >
        {errorSending}
      </ErrorSnackbar>

      <CopiedSnackbar open={copied} onClose={() => setCopied(false)} />

      <AsaasSeal sx={{ mt: 3 }} />
    </Box>
  )
}
